import { CityModel } from "../Models/CityModel";
import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared";
import { useCountryEnricher } from "./useCountryEnricher";

/**
 * =====================================================
 * Hook: useCityEnricher
 * =====================================================
 *
 * Description:
 * Custom hook to enrich City domain data with related users.
 * Combines user information into CityModel for detailed views.
 *
 * Responsibilities:
 * - Fetch and maintain related data (users)
 * - Track combined loading/error state
 * - Provide helper functions to enrich a single city, by ID, or a list
 *
 * Notes:
 * - Returns a pure API for enrichment; no side effects beyond data fetching.
 * - Compatible with detail and list views for cities.
 *
 * Author: Samano CASTRE
 * Date: February 1, 2026
 */
export function useCityEnricher() {

    /**
             * Centralized audit enrichment hook
             * - Provides O(1) user resolution via precomputed Map
             * - Exposes audit enrichment helpers and status/error
             */
    const {
        enricherResponse: auditEnricherResponse,
        getEnrichedAudit,
    } = useAuditEnricher<CityModel>();

    const { countryMap, response : countryEnricherResponse } = useCountryEnricher();


    /** =====================================================
     * Combined Loading & Error Status
     * =====================================================
     *
     * Combines status from all related hooks into a single ActionResponse:
     * - status: combined loading state (idle, loading, success, failed)
     * - error: first non-null error encountered
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(countryEnricherResponse.status, auditEnricherResponse.status),
        error: countryEnricherResponse.error ?? auditEnricherResponse.error
    };

    /** =====================================================
     * Public Helper Functions
     * =====================================================
     *
     * These functions enrich cities with:
     * - Audit info (created/updated by, timestamps)
     * - Related users (creator/updater)
     */


    // Enrich a list of cities
    const getEnrichedCityMap = ( cityMap: RecordMap<CityModel>): RecordMap<CityModel> => {
        return cityMap.map<CityModel>(getStrictEnrichedCity);
    }

    const getStrictEnrichedCity = (city: CityModel): CityModel => {
        return getEnrichedAudit(city);

    }

    // Enrich a single city with audit/user info
    const getEnrichedCity = (city?: CityModel): CityModel | undefined => {
        if (!city) return undefined;
        return getStrictEnrichedCity(city);
        
    }

    const getEnrichedCityById = (id?: number | null): CityModel | undefined => {
        if (!id) {
            return undefined;
        }
        const foundCity = countryMap.findMap(country =>
            country.cities.find(city => city.id === id)
        );
        
        return getEnrichedCity(foundCity);
    }

    /** =====================================================
     * Return API
     * =====================================================
     *
     * Provides all enriched data and helper functions
     * for consuming components.
     */
    return {
        cityMap : countryMap.flatMapArray(country => country.cities),
        countryMap,
        response,
        getEnrichedCity,
        getEnrichedCityById,
        getEnrichedCityMap
    }
}